<template>
  <div class="tx-list-page">
    <v-container fluid grid-list-xl>
      <v-layout row wrap>
        <v-flex xs12>
          <div class="list-hero">
            <p class="list-crumb">Transit / Aprobadas</p>
            <h1 class="list-title">Facturas Aprobadas</h1>
            <p class="list-subtitle">
              Consulta las facturas aprobadas y su estado de pago.
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <tx-filter @search="onSearch" @download="onDownload"></tx-filter>
        </v-flex>

        <v-flex xs12>
          <v-card class="tx-card">
            <div class="tx-toolbar">
              <div class="tx-toolbar__info">
                <span class="tx-chip">
                  <v-icon small class="tx-chip__icon">check_circle</v-icon>
                  {{ meta.totalGroups }} aprobadas
                  <template v-if="meta.totalInvoices !== meta.totalGroups">
                    ({{ meta.totalInvoices }} facturas)
                  </template>
                </span>
              </div>
            </div>
            <v-divider></v-divider>

            <v-card-text class="pa-0">
              <div class="table-wrap">
                <table class="tx-table">
                  <thead>
                    <tr>
                      <th># Factura</th>
                      <th>Distribuidor</th>
                      <th>Laboratorio</th>
                      <th class="th-center">Días en F&amp;D</th>
                      <th class="th-center">Detalles</th>
                      <th class="th-center">Pago</th>
                    </tr>
                  </thead>
                  <!-- Un <tbody> por trámite, como en Contabilidad: la banda
                       con el consecutivo y, debajo, una fila por factura. -->
                  <tbody
                    v-for="group in groups"
                    :key="group.key"
                    class="tx-group"
                  >
                    <!-- Banda del trámite (texto del mixin TramiteBand): las
                         facturas de un consolidado se aprobaron juntas y se
                         leen juntas. -->
                    <tr
                      class="tx-group-row"
                      :class="{ 'tx-group-row--single': !esConsolidado(group) }"
                    >
                      <td colspan="6">
                        <v-icon small class="tx-group-row__icon">{{
                          esConsolidado(group) ? 'layers' : 'description'
                        }}</v-icon>
                        {{ etiquetaTramite(group) }}
                        <strong>{{ consecutivoDe(group) }}</strong>
                        &middot; {{ facturasDe(group) }}
                        <span
                          v-if="esConsolidado(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; CIF total {{ money(cifTramiteDe(group)) }}
                        </span>
                        <span
                          v-if="controlFdDe(group)"
                          class="tx-group-row__muted"
                        >
                          &middot; Control F&amp;D: {{ controlFdDe(group) }}
                        </span>
                      </td>
                    </tr>

                    <tr
                      v-for="(item, index) in group.invoices"
                      :key="item.id"
                      :class="{ 'tx-child-row': esConsolidado(group) }"
                    >
                      <td class="tx-cell--strong tx-cell--factura">
                        {{ item.invoiceNumber || '—' }}
                      </td>
                      <td>{{ distName(item) }}</td>
                      <td>{{ labName(item) }}</td>
                      <td class="th-center">
                        <span class="tx-days">{{ item.totalInTransit }}</span>
                      </td>
                      <td class="th-center">
                        <v-btn
                          class="btn-detail"
                          icon
                          small
                          title="Ver detalles"
                          @click="handleDetails(item)"
                        >
                          <v-icon small>visibility</v-icon>
                        </v-btn>
                      </td>
                      <!-- Pago, una sola vez por trámite: en un consolidado la
                           celda abarca todas sus facturas, que se pagan
                           juntas desde Contabilidad. -->
                      <td
                        v-if="celdaTramite(group, index)"
                        :rowspan="filasTramite(group)"
                        class="th-center"
                        :class="{ 'tx-cell--tramite': esConsolidado(group) }"
                      >
                        <span
                          class="status-pill"
                          :class="pagoDe(group).clase"
                          :title="pagoDe(group).titulo"
                        >
                          {{ pagoDe(group).texto }}
                        </span>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-if="pagination.loading">
                    <tr>
                      <td colspan="6" class="empty-state">Cargando...</td>
                    </tr>
                  </tbody>
                  <tbody v-else-if="groups.length === 0">
                    <tr>
                      <td colspan="6" class="empty-state">
                        No se encontraron facturas aprobadas con los filtros
                        aplicados.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex xs12 v-if="pagination.pages">
          <div class="list-footer">
            <div class="list-footer__count">
              Mostrando {{ groups.length }} de {{ meta.totalGroups }} tramites
              ({{ meta.totalInvoices }}
              facturas)
            </div>
            <v-pagination
              v-model="pagination.page"
              :length="pagination.pages"
              :total-visible="7"
              color="#254f87"
              @input="asyncPagination"
            ></v-pagination>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="1024" v-if="detail.data" v-model="detail.show">
      <compose
        :key="detail.key"
        v-on:close-detail="detail.show = false"
        :item="detail.data"
      ></compose>
    </v-dialog>

    <v-dialog max-width="350" persistent v-model="loading">
      <loading></loading>
    </v-dialog>
  </div>
</template>

<script>
import compose from './../dialog/DialogDetails'
import loading from './../dialog/DialogLoading'
import TxFilter from './TransitFilter.vue'
import SessionHelper from '@/helpers/SessionHelper'
import TramiteBand from './mixins/TramiteBand'

export default {
  // Banda del trámite compartida con Contabilidad (consecutivo, facturas, CIF).
  mixins: [TramiteBand],
  components: {
    compose,
    loading,
    'tx-filter': TxFilter,
  },
  data() {
    return {
      loading: true,
      // El backend agrupa por consolidado, igual que Tramites en Proceso.
      groups: [],
      meta: { totalGroups: 0, totalInvoices: 0 },
      pagination: {
        page: 1,
        rpp: this.$config.resultsPerPage,
        totalItems: 0,
        loading: true,
        pages: 0,
        search: {},
      },
      detail: {
        show: null,
        data: null,
        key: 0,
      },
    }
  },
  created() {
    this.asyncPagination()
  },
  methods: {
    // Estado de pago del trámite. Un consolidado se paga entero; si alguna
    // vez quedara a medias, se dice cuántas van pagadas en vez de esconderlo.
    pagoDe(group) {
      const total = group.invoices.length
      const pagadas = group.invoices.filter(i => i.liquidated !== 'NO').length
      if (pagadas === total) {
        return { texto: 'Pagada', clase: 'status-pill--success', titulo: '' }
      }
      if (pagadas === 0) {
        return { texto: 'Pendiente', clase: 'status-pill--muted', titulo: '' }
      }
      return {
        texto: `Parcial ${pagadas}/${total}`,
        clase: 'status-pill--warning',
        titulo: `${pagadas} de ${total} facturas del consolidado pagadas`,
      }
    },
    distName(item) {
      return item.distribuidor || '—'
    },
    labName(item) {
      return item.laboratorio || '—'
    },
    helperPaginate(response) {
      this.groups = (response && response.groups) || []
      this.meta = {
        totalGroups: (response && response.totalGroups) || 0,
        totalInvoices: (response && response.totalInvoices) || 0,
      }
      // Se pagina por GRUPO: un consolidado no queda partido entre paginas.
      this.pagination.totalItems = this.meta.totalInvoices
      this.pagination.pages =
        Math.ceil(this.meta.totalGroups / this.pagination.rpp) || 0
      this.loading = false
      this.pagination.loading = false
    },
    asyncPagination() {
      this.pagination.loading = true
      const filter = {
        rpp: this.pagination.rpp,
        page: this.pagination.page,
        status: 'approved',
        ...this.pagination.search,
      }
      return new Promise(resolve => {
        this.$store
          .dispatch('invoices/getConsolidated', filter)
          .then(response => {
            this.helperPaginate(response)
            return resolve()
          })
      })
    },
    handleDetails(row) {
      this.loading = true
      this.$store
        .dispatch('invoices/getItems', { invoiceId: row.id })
        .then(items => {
          this.loading = false
          this.detail = {
            // El listado agrupado trae laboratorio/distribuidor como texto,
            // pero DialogDetails espera lab_invoices/dist_invoices.
            data: {
              ...row,
              lab_invoices: { id: row.labId, name: row.laboratorio },
              dist_invoices: { id: row.distId, name: row.distribuidor },
              products: items ? items : null,
            },
            show: true,
            key: this.detail.key + 1,
          }
        })
    },
    onSearch(params) {
      Object.keys(params).forEach(key => {
        if (params[key] == null || params[key] === '') delete params[key]
      })
      this.pagination.search = params
      this.pagination.page = 1
      this.asyncPagination()
    },
    onDownload() {
      const filter = {
        status: 'approved',
        ...this.pagination.search,
        token: SessionHelper.token,
      }
      this.$socket.emit('exportTransitInvoices', filter)
    },
  },
}
</script>

<style scoped src="./transit-list.css"></style>
